/**
 * Converts an abbreviated footwear size label (e.g., "M9 / W10.5")
 * into a screen reader-friendly, fully expanded string (e.g., "Men's 9 / Women's 10.5").
 *
 * The function:
 * - Checks whether the provided attribute selection label matches a supported sizing pattern.
 * - If so, returns a string with abbreviations replaced by their full, accessible names for screen readers.
 * - If the label is missing or does not match a supported pattern, returns undefined or the unformatted label respectively.
 *
 * @param label - The attribute selection label.
 * @returns The a screen reader-friendly label, an unformatted label, or undefined.
 * @category Utils
 */

export const formatAttributeSelectionLabelAbbreviations = (
  label?: string,
  abbreviations: Record<string, string> = {
    // English
    B: 'Boys\'',
    M: 'Men\'s',
    W: 'Women\'s',
    // French
    G: 'Garçons',
    F: 'Filles',
    H: 'Hommes',
  }
) => {
  if (!label) return

  // example: M9 / W10.5
  const pattern1 = /^([BMWGFH])(\d{1,2}(?:[.,]\d+)?) \/ ([BMWGFH])(\d{1,2}(?:[.,]\d+)?)$/
  const match1 = label.match(pattern1)
  if (match1) {
    const [, a1, n1, a2, n2] = match1
    return `${abbreviations[a1] || a1} ${n1}, ${abbreviations[a2] || a2} ${n2}`
  }

  // example: S (3.5-6M / 5-7.5W)
  const pattern2 = /^([A-Z]) \((\d{1,2}(?:[.,]\d+)?)-(\d{1,2}(?:[.,]\d+)?)([BMWGFH]) \/ (\d{1,2}(?:[.,]\d+)?)-(\d{1,2}(?:[.,]\d+)?)([BMWGFH])\)$/
  const match2 = label.match(pattern2)
  if (match2) {
    const [, size, n1a, n1b, a1, n2a, n2b, a2] = match2
    return `${size} (${abbreviations[a1] || a1} ${n1a} - ${n1b}, ${abbreviations[a2] || a2} ${n2a} - ${n2b})`
  }

  // If not a known pattern, return as-is
  return label
}
